import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  getClubTeams,
  getTeamSessions,
  getPointsSummary,
  getMyAttendanceHistory,
  type PointsSummary,
  type AttendanceRecord,
} from "../../services/api";
import { getAssignedQuizzes, AssignedQuiz } from "../../services/quizzes";
import { useAuth } from "../../context/AuthContext";
import Leaderboard from "../../components/Leaderboard";
import { formatDate } from "../../lib/dateFormat";

type TabId = "overview" | "sessions" | "quizzes" | "attendance" | "leaderboard";

export default function Sportiv() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clubs: ctxClubs, activeClubId: ctxClubId } = useAuth();

  const [clubId, setClubId] = useState<string | null>(ctxClubId);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [assignedQuizzes, setAssignedQuizzes] = useState<AssignedQuiz[]>([]);
  const [pointsSummary, setPointsSummary] = useState<PointsSummary | null>(null);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceRecord[]>([]);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [loading, setLoading] = useState(true);

  // Sync club from context
  useEffect(() => {
    if (ctxClubId) setClubId(ctxClubId);
    else if (ctxClubs.length > 0) setClubId(ctxClubs[0].id);
  }, [ctxClubId, ctxClubs]);

  // Load teams when club changes
  useEffect(() => {
    (async () => {
      if (!clubId) return;
      const ts = await getClubTeams(clubId);
      setTeams(ts);
      setTeamId(ts[0]?.id || null);
    })();
  }, [clubId]);

  // Load sessions when team changes
  useEffect(() => {
    (async () => {
      if (!teamId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const ss = await getTeamSessions(teamId);
      const future = ss.filter((s: any) => new Date(s.start_at) > new Date());
      setSessions(future);
      setLoading(false);
    })();
  }, [teamId]);

  // Load quizzes, points, and attendance on mount
  useEffect(() => {
    loadAssignedQuizzes();
    loadPointsSummary();
    loadAttendanceHistory();
  }, []);

  const loadAssignedQuizzes = async () => {
    try {
      const quizzes = await getAssignedQuizzes();
      setAssignedQuizzes(quizzes);
    } catch (error) {
      console.error("Failed to load assigned quizzes:", error);
    }
  };

  const loadPointsSummary = async () => {
    try {
      const summary = await getPointsSummary();
      setPointsSummary(summary);
    } catch (error) {
      console.error("Failed to load points summary:", error);
    }
  };

  const loadAttendanceHistory = async () => {
    try {
      const history = await getMyAttendanceHistory();
      setAttendanceHistory(history);
    } catch (error) {
      console.error("Failed to load attendance history:", error);
    }
  };

  const completedQuizzes = assignedQuizzes.filter(({ quiz }) => quiz.user_attempt_count > 0).length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "present": return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
      case "late": return "bg-amber-500/20 text-amber-300 border-amber-500/30";
      case "absent": return "bg-red-500/20 text-red-300 border-red-500/30";
      case "excused": return "bg-slate-500/20 text-slate-300 border-slate-500/30";
      default: return "bg-slate-500/20 text-slate-300 border-slate-500/30";
    }
  };

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    {
      id: "overview",
      label: t("athlete.overview", "Overview"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      id: "sessions",
      label: t("athlete.sessions", "Sessions"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: "quizzes",
      label: t("athlete.quizzes", "Quizzes"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
    {
      id: "attendance",
      label: t("athlete.attendance", "Attendance"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      id: "leaderboard",
      label: t("athlete.leaderboard", "Leaderboard"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            {t("dash.athlete", "Athlete Dashboard")}
          </h1>
          <p className="text-slate-400 mt-1">
            {t("athlete.subtitle", "Track your progress, sessions, and achievements")}
          </p>
        </div>

        {/* Team Selector */}
        <div className="flex items-center gap-3">
          <select
            value={teamId || ""}
            onChange={(e) => setTeamId(e.target.value || null)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            disabled={!teams.length}
          >
            <option value="" disabled>{t("common.selectTeam", "Select Team")}</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {loading && (
            <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-400">{t("athlete.totalPoints", "Total Points")}</p>
              <p className="text-xl font-bold text-white">{pointsSummary?.total || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-400">{t("athlete.upcoming", "Upcoming")}</p>
              <p className="text-xl font-bold text-white">{sessions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-400">{t("athlete.quizzes", "Quizzes")}</p>
              <p className="text-xl font-bold text-white">{assignedQuizzes.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-slate-400">{t("athlete.completed", "Completed")}</p>
              <p className="text-xl font-bold text-white">{completedQuizzes}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-1.5">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Points Summary Card */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("athlete.myPoints", "My Points")}</h2>
              </div>

              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-emerald-400">{pointsSummary?.total || 0}</p>
                <p className="text-slate-400 mt-1">{t("athlete.totalPoints", "Total Points")}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-400">{pointsSummary?.this_week || 0}</p>
                  <p className="text-xs text-slate-400">{t("athlete.thisWeek", "This Week")}</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-400">{pointsSummary?.this_month || 0}</p>
                  <p className="text-xs text-slate-400">{t("athlete.thisMonth", "This Month")}</p>
                </div>
              </div>

              {/* Points Breakdown */}
              {pointsSummary?.breakdown && pointsSummary.breakdown.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-3">{t("athlete.breakdown", "Breakdown")}</h3>
                  <div className="space-y-2">
                    {pointsSummary.breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                        <span className="text-sm text-slate-300 capitalize">{item.source}</span>
                        <span className="text-sm font-semibold text-emerald-400">+{item.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Upcoming Sessions */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-white">{t("athlete.upcomingSessions", "Upcoming Sessions")}</h2>
                </div>
                <button onClick={() => setActiveTab("sessions")} className="text-sm text-emerald-400 hover:text-emerald-300 font-medium">
                  {t("common.viewAll", "View all")}
                </button>
              </div>

              <div className="space-y-3 max-h-[320px] overflow-y-auto">
                {sessions.slice(0, 5).map((s) => (
                  <div key={s.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-blue-500/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-white">{s.title}</h4>
                        <p className="text-xs text-slate-400 mt-1">{new Date(s.start_at).toLocaleString()}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        s.type === "sport" ? "bg-emerald-500/20 text-emerald-300" : "bg-purple-500/20 text-purple-300"
                      }`}>
                        {s.type}
                      </span>
                    </div>
                  </div>
                ))}
                {sessions.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 bg-slate-700/50 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-slate-400 text-sm">{t("athlete.noSessions", "No upcoming sessions")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === "sessions" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("athlete.upcomingSessions", "Upcoming Sessions")}</h2>
              </div>
              <span className="text-sm text-slate-400">{sessions.length} {t("common.total", "total")}</span>
            </div>

            <div className="space-y-3">
              {sessions.map((s) => (
                <div key={s.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-blue-500/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-white">{s.title}</h4>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          {formatDate(new Date(s.start_at))}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          {new Date(s.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {s.location_text && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                            {s.location_text}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      s.type === "sport" ? "bg-emerald-500/20 text-emerald-300" : "bg-purple-500/20 text-purple-300"
                    }`}>
                      {s.type}
                    </span>
                  </div>
                </div>
              ))}
              {sessions.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-slate-400">{t("athlete.noSessions", "No upcoming sessions")}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quizzes Tab */}
        {activeTab === "quizzes" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("athlete.assignedQuizzes", "Assigned Quizzes")}</h2>
              </div>
              <span className="text-sm text-slate-400">{assignedQuizzes.length} {t("common.available", "available")}</span>
            </div>

            {assignedQuizzes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assignedQuizzes.map(({ assignment, quiz }) => {
                  const isCompleted = quiz.user_attempt_count > 0;
                  return (
                    <div
                      key={quiz.id}
                      className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-purple-500/50 transition-all cursor-pointer group"
                      onClick={() => navigate(`/quiz/${quiz.id}`)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isCompleted ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"
                        }`}>
                          {isCompleted ? t("quiz.completed", "Completed") : t("quiz.new", "New")}
                        </span>
                        {quiz.category && (
                          <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-lg">{quiz.category}</span>
                        )}
                      </div>
                      <h3 className="font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors">{quiz.title}</h3>
                      {quiz.description && (
                        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{quiz.description}</p>
                      )}
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm">{quiz.question_count} questions</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-amber-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium">{quiz.reward_points} pts</span>
                        </div>
                      </div>
                      <button className={`w-full px-4 py-2 rounded-xl font-medium transition-colors ${
                        isCompleted
                          ? "bg-slate-700 hover:bg-slate-600 text-slate-300"
                          : "bg-purple-500 hover:bg-purple-600 text-white"
                      }`}>
                        {isCompleted ? t("quiz.viewResults", "View Results") : t("quiz.startQuiz", "Start Quiz")}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t("quiz.noQuizzesAssigned", "No Quizzes Assigned Yet")}</h3>
                <p className="text-slate-400 text-sm">{t("quiz.noQuizzesDescription", "Your coach will assign educational quizzes for you to complete")}</p>
              </div>
            )}
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === "attendance" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("athlete.attendanceHistory", "Attendance History")}</h2>
              </div>
              <span className="text-sm text-slate-400">{attendanceHistory.length} {t("common.records", "records")}</span>
            </div>

            {/* Attendance Stats */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-emerald-400">{attendanceHistory.filter(a => a.status === "present").length}</p>
                <p className="text-xs text-emerald-300/70">{t("attendance.present", "Present")}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-amber-400">{attendanceHistory.filter(a => a.status === "late").length}</p>
                <p className="text-xs text-amber-300/70">{t("attendance.late", "Late")}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-red-400">{attendanceHistory.filter(a => a.status === "absent").length}</p>
                <p className="text-xs text-red-300/70">{t("attendance.absent", "Absent")}</p>
              </div>
              <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-slate-400">{attendanceHistory.filter(a => a.status === "excused").length}</p>
                <p className="text-xs text-slate-300/70">{t("attendance.excused", "Excused")}</p>
              </div>
            </div>

            <div className="space-y-3">
              {attendanceHistory.map((record) => (
                <div key={record.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-white">{record.session.title}</h4>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-400">
                        <span>{record.session.team.name}</span>
                        <span>{formatDate(new Date(record.session.start_at))}</span>
                      </div>
                      {record.reason && (
                        <p className="text-sm text-slate-500 mt-2 italic">"{record.reason}"</p>
                      )}
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                      {record.status}
                    </span>
                  </div>
                </div>
              ))}
              {attendanceHistory.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-slate-400">{t("athlete.noAttendance", "No attendance records yet")}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-white">{t("athlete.leaderboard", "Leaderboard")}</h2>
            </div>
            <Leaderboard limit={15} />
          </div>
        )}
      </div>
    </div>
  );
}
